import React, { useCallback } from 'react';
import { i18n } from '@international/mastodon-i18n';

import IntlLoginForm from 'components/Reports/IntlLoginForm';
import Loading from 'components/Loading';
import MainContainer from 'components/MainContainer';
import { fetchCandidateReportsIntl, clearLoggedOut } from 'actions';
import ToastNotification from 'components/ToastNotification';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { safeReturnPath } from 'utils/returnPath';
import useUnifiedLoginKickoff from 'utils/useUnifiedLoginKickoff';
import useAuthSessionBootstrap from 'utils/useAuthSessionBootstrap';
import useCandidateHydration from 'utils/useCandidateHydration';
import { PATHS } from '../../../../constants';
import useAuthGateState from '../../../../hooks/useAuthGateState';
import useIsomorphicLayoutEffect from '../../../../hooks/useIsomorphicLayoutEffect';

const InternationalPrivacyLogin: React.FC = () => {
  const { processing, error } = useSelector((state: any) => state.portal);
  const {
    portalAuth: { loggedOut, loggedIn },
  } = useAuthGateState();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { holdsLogin: unifiedKickoffHoldsLogin } = useUnifiedLoginKickoff();
  // Outside Layout, so this route owns the bridge into portal state, but
  // only for an inbound unified-login session.
  const { inbound } = useAuthSessionBootstrap({ requireInbound: true });
  useCandidateHydration({ enabled: inbound });
  const handleSubmit = useCallback(
    (values: any) => {
      dispatch(fetchCandidateReportsIntl(values));
    },
    [dispatch],
  );

  useIsomorphicLayoutEffect(() => {
    if (loggedIn) {
      navigate(
        safeReturnPath(state?.prevLocation) ||
          `${PATHS.INTERNATIONAL}/privacy/delete`,
      );
    } else if (error) {
      navigate('#login_error', { state });
    }
  }, [error, loggedIn, navigate, state]);

  if (unifiedKickoffHoldsLogin) {
    return (
      <MainContainer>
        <Loading />
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      {loggedOut && (
        <ToastNotification
          title={i18n.getStr('components.successFullyLoggedOut')}
          kind='success'
          onClose={() => {
            dispatch(clearLoggedOut());
          }}
        />
      )}
      <IntlLoginForm
        onSubmit={handleSubmit}
        processing={processing}
        fetchError={error}
        loginSource='InternationalLoginForm'
      />
    </MainContainer>
  );
};

export default InternationalPrivacyLogin;
